import type {
  ConnectionOperation,
  EntityFieldItemsUpdater,
  UpdateEntityFieldItemsOperation,
} from '@github-ui/optimistic-connection'

import type {
  PendingSuggestion,
  RecentAgentActivityFragment,
} from '../components/recent-agent-activity/use-recent-agent-activity'

/**
 * The apply and reject pending-suggestion mutations accept structurally
 * identical "suggestion refs". We define the union once and derive both the
 * GraphQL input and optimistic update from it, so the two mutations stay in sync.
 */
export type SuggestionRef =
  | {id: string; kind: 'TYPE'; issueTypeId: string}
  | {id: string; kind: 'LABEL'; labelId: string}
  | {id: string; kind: 'FIELD'; issueFieldId: string}
  | {id: string; kind: 'CLOSE'}
  | {id: string; kind: 'ASSIGNEE'; assigneeId: string}

type SuggestionInput = {
  kind: SuggestionRef['kind']
  issueTypeId?: string
  labelId?: string
  issueFieldId?: string
  assigneeId?: string
}

/** Translate a suggestion ref into the mutation input object. */
export function toSuggestionInput(ref: SuggestionRef): SuggestionInput {
  switch (ref.kind) {
    case 'TYPE':
      return {kind: 'TYPE', issueTypeId: ref.issueTypeId}
    case 'LABEL':
      return {kind: 'LABEL', labelId: ref.labelId}
    case 'FIELD':
      return {kind: 'FIELD', issueFieldId: ref.issueFieldId}
    case 'ASSIGNEE':
      return {kind: 'ASSIGNEE', assigneeId: ref.assigneeId}
    case 'CLOSE':
      return {kind: 'CLOSE'}
  }
}

// Apply and reject refs share the same shape, so one builder serves both mutations.
export function buildSuggestionRef(suggestion: PendingSuggestion): SuggestionRef | null {
  if (suggestion.type === 'type_set' && suggestion.issueTypeId) {
    return {id: suggestion.id, kind: 'TYPE', issueTypeId: suggestion.issueTypeId}
  }
  if (suggestion.type === 'label_added' && suggestion.labelId) {
    return {id: suggestion.id, kind: 'LABEL', labelId: suggestion.labelId}
  }
  if (suggestion.type === 'field_set' && suggestion.issueFieldId) {
    return {id: suggestion.id, kind: 'FIELD', issueFieldId: suggestion.issueFieldId}
  }
  if (suggestion.type === 'issue_closed') {
    return {id: suggestion.id, kind: 'CLOSE'}
  }
  if (suggestion.type === 'assignee_assigned' && suggestion.assigneeId) {
    return {id: suggestion.id, kind: 'ASSIGNEE', assigneeId: suggestion.assigneeId}
  }
  return null
}

export type PendingSuggestionMutationOptions<TData> = {
  issueId: string
  owner: string
  repository: string
  issueNumber: number
  onCompleted?: (data: TData) => void
  onError?: (error: Error) => void
}

type SuggestionStateUpdate = {
  suggestionIds: string[]
  state: 'APPLIED' | 'REJECTED'
}

export function pendingSuggestionOperation(
  issueId: string,
  update: SuggestionStateUpdate,
): UpdateEntityFieldItemsOperation {
  return {
    type: 'updateEntityFieldItems',
    id: issueId,
    field: 'pendingSuggestions',
    upserts: [...new Set(update.suggestionIds)].map(key => ({key, item: {state: update.state}})),
    removeKeys: [],
    countDelta: 0,
  }
}

function suggestionStatesOf(operation: ConnectionOperation): Map<string, SuggestionStateUpdate['state']> {
  if (operation.type !== 'updateEntityFieldItems' || operation.field !== 'pendingSuggestions') {
    throw new Error('Expected a pending suggestion update')
  }

  const states = new Map<string, SuggestionStateUpdate['state']>()
  for (const {key, item} of operation.upserts) {
    if (item.state !== 'APPLIED' && item.state !== 'REJECTED') {
      throw new Error('Invalid pending suggestion state')
    }
    states.set(key, item.state)
  }
  return states
}

export const applyPendingSuggestionOperation: EntityFieldItemsUpdater = (entity, operation) => {
  if (operation.field !== 'pendingSuggestions') return false
  const states = suggestionStatesOf(operation)
  const pending = entity.pendingSuggestions as RecentAgentActivityFragment['pendingSuggestions']
  if (!pending) return false
  let updated = false

  entity.pendingSuggestions = pending.map(suggestion => {
    const state = states.get(suggestion.id)
    if (!state) return suggestion
    updated = true
    return withSuggestionState(suggestion, state)
  })
  return updated
}

function withSuggestionState(
  suggestion: NonNullable<RecentAgentActivityFragment['pendingSuggestions']>[number],
  state: SuggestionStateUpdate['state'],
) {
  switch (suggestion.__typename) {
    case 'PendingTypeSuggestion':
      return {...suggestion, typeState: state}
    case 'PendingLabelSuggestion':
      return {...suggestion, labelState: state}
    case 'PendingFieldSuggestion':
      return {...suggestion, fieldState: state}
    case 'PendingCloseSuggestion':
      return {...suggestion, closeState: state}
    case 'PendingAssigneeSuggestion':
      return {...suggestion, assigneeState: state}
  }
}
